import { useEffect, useMemo, useRef } from "react";
import type { RefObject } from "react";

import {
    computeDocumentSectionScrollProgress,
    getNearestVerticalScrollContainer
} from "./use-spiderweb-document-section-progress";

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

/**
 * Sanitizes anchor values as **scroll progress percent 0–100**, converts to
 * **0–1** for comparison with `computeDocumentSectionScrollProgress`, sorts
 * ascending, and drops duplicates (to ~1e-5 in normalized space).
 */
export function normalizeScrollMagnetAnchors(
    raw: readonly number[] | undefined
): number[] {
    if (!raw?.length) {
        return [];
    }
    const roundedKeys = new Set<number>();
    const out: number[] = [];
    for (const x of raw) {
        if (!Number.isFinite(x)) {
            continue;
        }
        const pct = Math.min(100, Math.max(0, x));
        const c = clamp01(pct / 100);
        const key = Math.round(c * 1e5);
        if (roundedKeys.has(key)) {
            continue;
        }
        roundedKeys.add(key);
        out.push(c);
    }
    out.sort((a, b) => a - b);
    return out;
}

/**
 * Picks the next anchor in the **positive scroll / higher progress** direction:
 * the smallest anchor `a` with `a >= progress` (anchors must be sorted ascending).
 * If progress is already past every anchor, returns the last anchor.
 */
export function forwardBiasedScrollMagnetProgress(
    progress: number,
    anchors: readonly number[]
): number | null {
    if (anchors.length === 0) {
        return null;
    }
    const p = clamp01(progress);
    const firstAtOrAhead = anchors.find((a) => a >= p);
    if (firstAtOrAhead !== undefined) {
        return firstAtOrAhead;
    }
    const tail = anchors.at(-1);
    return tail === undefined ? null : tail;
}

function pickNearestByMetric(
    values: readonly number[],
    metric: (v: number) => number
): number | undefined {
    const first = values[0];
    if (first === undefined) {
        return undefined;
    }
    let best = first;
    let bestM = metric(best);
    for (let i = 1; i < values.length; i++) {
        const v = values[i];
        if (v === undefined) {
            continue;
        }
        const m = metric(v);
        if (m < bestM) {
            bestM = m;
            best = v;
        }
    }
    return best;
}

/**
 * Picks the nearest anchor **in the direction of the last scroll** (pixel delta:
 * positive ≈ scrollTop increased / “down” → higher progress anchors). If there
 * is no anchor strictly ahead (or behind) of progress in that direction, uses
 * the last (or first) anchor. When scroll delta is within `scrollDeadbandPx`,
 * falls back to the nearest anchor by absolute progress distance.
 */
export function directionNearestScrollMagnetProgress(
    progress: number,
    anchors: readonly number[],
    lastScrollDeltaPx: number,
    scrollDeadbandPx: number
): number | null {
    if (anchors.length === 0) {
        return null;
    }
    const p = clamp01(progress);
    const dead = scrollDeadbandPx;
    const ahead = anchors.filter((a) => a > p);
    const behind = anchors.filter((a) => a < p);

    if (lastScrollDeltaPx > dead) {
        const pick = pickNearestByMetric(ahead, (a) => a - p);
        if (pick !== undefined) {
            return pick;
        }
        const tail = anchors.at(-1);
        return tail === undefined ? null : tail;
    }
    if (lastScrollDeltaPx < -dead) {
        const pick = pickNearestByMetric(behind, (a) => p - a);
        if (pick !== undefined) {
            return pick;
        }
        return anchors[0] ?? null;
    }
    const pick = pickNearestByMetric(anchors, (a) => Math.abs(a - p));
    return pick === undefined ? null : pick;
}

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

type ScrollMagnetEndpoints =
    | { mode: "window"; startY: number; endY: number }
    | { mode: "element"; el: HTMLElement; startTop: number; endTop: number };

function getScrollMagnetEndpoints(
    section: HTMLElement,
    scrollContainer: HTMLElement | null,
    progress: number
): ScrollMagnetEndpoints | null {
    const p = clamp01(progress);
    if (scrollContainer) {
        const maxScroll = Math.max(
            0,
            scrollContainer.scrollHeight - scrollContainer.clientHeight
        );
        if (maxScroll <= 1) {
            return null;
        }
        return {
            mode: "element",
            el: scrollContainer,
            startTop: scrollContainer.scrollTop,
            endTop: p * maxScroll
        };
    }

    const trackH = section.offsetHeight;
    const viewH = window.innerHeight;
    const scrollable = trackH - viewH;
    if (scrollable <= 1) {
        return null;
    }
    const rect = section.getBoundingClientRect();
    const endY = window.scrollY + rect.top + p * scrollable;
    return {
        mode: "window",
        startY: window.scrollY,
        endY: Math.max(0, endY)
    };
}

function runScrollMagnetEase(
    endpoints: ScrollMagnetEndpoints,
    durationMs: number,
    isCancelled: () => boolean,
    onRafScheduled: (id: number) => void,
    onRafCleared: () => void
): void {
    const startTime = performance.now();

    const step = () => {
        if (isCancelled()) {
            onRafCleared();
            return;
        }
        const now = performance.now();
        const t = Math.min(1, (now - startTime) / durationMs);
        const e = easeInOutCubic(t);
        if (endpoints.mode === "window") {
            const y =
                endpoints.startY + (endpoints.endY - endpoints.startY) * e;
            window.scrollTo(0, y);
        } else {
            const top =
                endpoints.startTop +
                (endpoints.endTop - endpoints.startTop) * e;
            endpoints.el.scrollTop = top;
        }
        if (t < 1) {
            onRafScheduled(requestAnimationFrame(step));
        } else {
            onRafCleared();
        }
    };

    onRafScheduled(requestAnimationFrame(step));
}

export type UseSpiderwebScrollMagnetOptions = {
    enabled: boolean;
    anchors: readonly number[] | undefined;
    /** Skip nudging when already within this distance of any anchor. */
    minProgressDelta?: number;
    /** Idle time with no `scroll` events before we may nudge (ms). */
    settleDebounceMs?: number;
    /** After a programmatic nudge, ignore scroll settling for this long (ms). */
    suppressSettleMs?: number;
    /**
     * How long the eased scroll-to-anchor runs (ms). Slightly longer than the
     * browser’s built-in `smooth` default for a more gradual feel.
     */
    scrollMagnetDurationMs?: number;
    /**
     * Ignore scroll deltas with magnitude below this (px) when inferring direction
     * for anchor pick; within deadband, the nearest anchor by progress wins.
     */
    scrollMagnetDirectionDeadbandPx?: number;
};

const DEFAULT_MIN_DELTA = 0.004;
/** No `scroll` events for this long before we consider a settle (ms). */
const DEFAULT_SETTLE_MS = 200;
const DEFAULT_SUPPRESS_MS = 380;
/** Eased magnet scroll; a bit longer than typical native `smooth` scrolling. */
const DEFAULT_MAGNET_DURATION_MS = 2000;
const DEFAULT_DIRECTION_DEADBAND_PX = 2;

/**
 * After the user stops scrolling (no `scroll` events for
 * `settleDebounceMs`), confirms scroll position is stable for one frame, then if
 * progress is not already within `minProgressDelta` of an anchor, scrolls smoothly
 * to a chosen anchor: nearest anchor in the **direction of the last user scroll**
 * (higher progress when scrolling “down”, lower when scrolling “up”); if scroll
 * intent is unclear, nearest by absolute progress distance.
 * Uses a custom ease-in-out over `scrollMagnetDurationMs` rather than the
 * browser’s `scroll-behavior: smooth` timing.
 *
 * Disabled when `prefers-reduced-motion` is handled by passing `enabled: false`.
 */
export function useSpiderwebScrollMagnet(
    sectionRef: RefObject<HTMLElement | null> | undefined,
    {
        enabled,
        anchors,
        minProgressDelta = DEFAULT_MIN_DELTA,
        settleDebounceMs = DEFAULT_SETTLE_MS,
        suppressSettleMs = DEFAULT_SUPPRESS_MS,
        scrollMagnetDurationMs = DEFAULT_MAGNET_DURATION_MS,
        scrollMagnetDirectionDeadbandPx = DEFAULT_DIRECTION_DEADBAND_PX
    }: UseSpiderwebScrollMagnetOptions
): void {
    const anchorsKey = useMemo(
        () =>
            anchors === undefined || anchors.length === 0
                ? ""
                : anchors.map((a) => Number(a).toFixed(5)).join("|"),
        [anchors]
    );

    const normalizedAnchors = useMemo(() => {
        if (anchorsKey === "") {
            return [];
        }
        const parsed = anchorsKey.split("|").map((s) => Number(s));
        return normalizeScrollMagnetAnchors(parsed);
    }, [anchorsKey]);

    const minDeltaRef = useRef(minProgressDelta);
    const settleMsRef = useRef(settleDebounceMs);
    const suppressMsRef = useRef(suppressSettleMs);
    const durationMsRef = useRef(scrollMagnetDurationMs);
    const directionDeadbandPxRef = useRef(scrollMagnetDirectionDeadbandPx);
    minDeltaRef.current = minProgressDelta;
    settleMsRef.current = settleDebounceMs;
    suppressMsRef.current = suppressSettleMs;
    durationMsRef.current = scrollMagnetDurationMs;
    directionDeadbandPxRef.current = scrollMagnetDirectionDeadbandPx;

    useEffect(() => {
        if (
            !enabled ||
            !sectionRef ||
            normalizedAnchors.length === 0 ||
            minProgressDelta <= 0
        ) {
            return;
        }

        let cancelled = false;
        let pollRafId = 0;
        let detach: (() => void) | undefined;
        let settleTimerId = 0;
        let suppressUntil = 0;
        let settleVerifyRafId = 0;

        const attach = (section: HTMLElement) => {
            const scrollContainer = getNearestVerticalScrollContainer(section);
            let magnetAnimRaf = 0;
            let lastScrollYForIntent = scrollContainer
                ? scrollContainer.scrollTop
                : window.scrollY;
            let lastIntentScrollDeltaPx = 0;

            const getScrollY = (): number =>
                scrollContainer ? scrollContainer.scrollTop : window.scrollY;

            const clearMagnetAnim = () => {
                if (magnetAnimRaf !== 0) {
                    cancelAnimationFrame(magnetAnimRaf);
                    magnetAnimRaf = 0;
                }
            };

            const tryMagnet = () => {
                if (cancelled || performance.now() < suppressUntil) {
                    return;
                }
                const p = computeDocumentSectionScrollProgress(
                    section,
                    scrollContainer
                );
                const minD = minDeltaRef.current;
                for (const a of normalizedAnchors) {
                    if (Math.abs(a - p) < minD) {
                        return;
                    }
                }
                const target = directionNearestScrollMagnetProgress(
                    p,
                    normalizedAnchors,
                    lastIntentScrollDeltaPx,
                    directionDeadbandPxRef.current
                );
                if (target === null) {
                    return;
                }
                const endpoints = getScrollMagnetEndpoints(
                    section,
                    scrollContainer,
                    target
                );
                if (!endpoints) {
                    return;
                }
                const durationMs = Math.max(120, durationMsRef.current);
                const startPx =
                    endpoints.mode === "window"
                        ? endpoints.startY
                        : endpoints.startTop;
                const endPx =
                    endpoints.mode === "window"
                        ? endpoints.endY
                        : endpoints.endTop;
                if (Math.abs(endPx - startPx) < 0.5) {
                    return;
                }

                clearMagnetAnim();
                suppressUntil =
                    performance.now() +
                    durationMs +
                    Math.max(120, suppressMsRef.current);

                runScrollMagnetEase(
                    endpoints,
                    durationMs,
                    () => cancelled,
                    (id) => {
                        magnetAnimRaf = id;
                    },
                    () => {
                        magnetAnimRaf = 0;
                    }
                );
            };

            const scheduleSettle = () => {
                if (cancelled) {
                    return;
                }
                if (settleVerifyRafId !== 0) {
                    cancelAnimationFrame(settleVerifyRafId);
                    settleVerifyRafId = 0;
                }
                if (performance.now() < suppressUntil) {
                    window.clearTimeout(settleTimerId);
                    settleTimerId = 0;
                    return;
                }
                window.clearTimeout(settleTimerId);
                settleTimerId = window.setTimeout(() => {
                    settleTimerId = 0;
                    const yIdle = getScrollY();
                    settleVerifyRafId = requestAnimationFrame(() => {
                        settleVerifyRafId = 0;
                        if (cancelled || performance.now() < suppressUntil) {
                            return;
                        }
                        if (Math.abs(getScrollY() - yIdle) > 0.5) {
                            return;
                        }
                        tryMagnet();
                    });
                }, settleMsRef.current);
            };

            const onScroll = () => {
                const y = getScrollY();
                if (performance.now() < suppressUntil) {
                    lastScrollYForIntent = y;
                } else {
                    const delta = y - lastScrollYForIntent;
                    lastScrollYForIntent = y;
                    const db = directionDeadbandPxRef.current;
                    if (Math.abs(delta) >= db) {
                        lastIntentScrollDeltaPx = delta;
                    }
                }
                scheduleSettle();
            };

            const scrollTarget = scrollContainer ?? window;
            scrollTarget.addEventListener("scroll", onScroll, {
                passive: true
            });

            detach = () => {
                window.clearTimeout(settleTimerId);
                settleTimerId = 0;
                if (settleVerifyRafId !== 0) {
                    cancelAnimationFrame(settleVerifyRafId);
                    settleVerifyRafId = 0;
                }
                clearMagnetAnim();
                scrollTarget.removeEventListener("scroll", onScroll);
            };
        };

        const el = sectionRef.current;
        if (el) {
            attach(el);
            return () => {
                cancelled = true;
                detach?.();
            };
        }

        const poll = () => {
            if (cancelled) {
                return;
            }
            const node = sectionRef.current;
            if (node) {
                attach(node);
                return;
            }
            pollRafId = requestAnimationFrame(poll);
        };
        pollRafId = requestAnimationFrame(poll);

        return () => {
            cancelled = true;
            cancelAnimationFrame(pollRafId);
            detach?.();
        };
    }, [enabled, normalizedAnchors, sectionRef]);
}
